import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ExternalLink, Monitor, Moon, RotateCw, Smartphone, Sun } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { originOf } from './storefrontConfig'

// Message names shared with storefront/src/lib/livePreview.ts.
const PREVIEW_MESSAGE = 'bhookly:design-preview'
const PREVIEW_READY = 'bhookly:preview-ready'

// A device is as wide as `w` and as tall as the stage allows, between these
// bounds, so the preview is always the largest whole screen that fits.
const DEVICES = {
  phone: { w: 390, minH: 640, maxH: 844 },
  desktop: { w: 1280, minH: 760, maxH: 1100 },
} as const
type Device = keyof typeof DEVICES
export type PreviewMode = 'light' | 'dark'

// Space the device frame adds around the screen, in unscaled pixels.
const BEZEL = { phone: 10, desktop: 0 } as const
const BROWSER_BAR = 36
const STAGE_PAD = 24

interface Props {
  /** The storefront's public address (storefront_base_url). */
  base: string
  onBase: (v: string) => void
  /** The unsaved design, in /public/settings field names. */
  design: Record<string, unknown>
  /** A link that opens the same preview in a new tab. */
  openUrl: string | null
  /** Whether the design opens dark; the preview follows it until they toggle. */
  opensDark: boolean
  /** Drawn in the phone instead of the live frame when no address is set. */
  fallback: (mode: PreviewMode) => React.ReactNode
}

/**
 * The preview stage: the tenant's real storefront in a device frame, restyled
 * live as they edit and sized to fit whatever room the stage has.
 *
 * Every change is posted to the frame (debounced) and applied there on top of
 * the saved settings, in that frame only — nothing is saved until they press
 * Save. The storefront only obeys this page's origin (its preview_origin) and
 * its container only lets this origin frame it.
 */
export function LivePreview({ base, onBase, design, openUrl, opensDark, fallback }: Props) {
  const origin = originOf(base)
  const frameRef = useRef<HTMLIFrameElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const [device, setDevice] = useState<Device>('phone')
  const [mode, setMode] = useState<PreviewMode>(opensDark ? 'dark' : 'light')
  const [ready, setReady] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [box, setBox] = useState({ w: 420, h: 640 })
  const [draftBase, setDraftBase] = useState(base)
  const [editing, setEditing] = useState(false)
  const [slow, setSlow] = useState(false)

  useEffect(() => setDraftBase(base), [base])
  useEffect(() => setMode(opensDark ? 'dark' : 'light'), [opensDark])

  // The frame announces itself once its data has loaded; only then can it
  // take a design.
  useEffect(() => {
    setReady(false)
    setSlow(false)
    if (!origin) return
    const timer = window.setTimeout(() => setSlow(true), 10000)
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== origin || e.source !== frameRef.current?.contentWindow) return
      if ((e.data as { type?: unknown })?.type === PREVIEW_READY) setReady(true)
    }
    window.addEventListener('message', onMessage)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('message', onMessage)
    }
  }, [origin, reloadKey])

  // Stream the design in, debounced so typing a hex code doesn't flood it.
  useEffect(() => {
    if (!ready || !origin) return
    const t = window.setTimeout(() => {
      frameRef.current?.contentWindow?.postMessage({ type: PREVIEW_MESSAGE, design, mode }, origin)
    }, 120)
    return () => window.clearTimeout(t)
  }, [design, mode, ready, origin])

  useLayoutEffect(() => {
    const el = boxRef.current
    if (!el) return
    // A hidden stage measures 0; keep the last real size rather than collapse.
    const measure = () => {
      if (el.clientWidth > 0 && el.clientHeight > 0) setBox({ w: el.clientWidth, h: el.clientHeight })
    }
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    measure()
    return () => ro.disconnect()
  }, [])

  // Without an address there is only the phone-sized sample to show.
  const shown: Device = origin ? device : 'phone'
  const { w, minH, maxH } = DEVICES[shown]
  const chromeW = BEZEL[shown] * 2
  const chromeH = BEZEL[shown] * 2 + (shown === 'desktop' ? BROWSER_BAR : 0)
  const availW = Math.max(120, box.w - STAGE_PAD * 2)
  const availH = Math.max(160, box.h - STAGE_PAD * 2)
  const scale = Math.min(1, availW / (w + chromeW), availH / (minH + chromeH))
  const h = Math.round(Math.max(minH, Math.min(maxH, availH / scale - chromeH)))
  const outerW = w + chromeW
  const outerH = h + chromeH

  const connect = () => {
    onBase(draftBase.trim())
    setEditing(false)
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b bg-card px-3 py-2">
        {origin && (
          <Segmented label="Screen size">
            <SegmentedItem active={device === 'phone'} onClick={() => setDevice('phone')} label="Phone">
              <Smartphone className="h-4 w-4" /> Phone
            </SegmentedItem>
            <SegmentedItem active={device === 'desktop'} onClick={() => setDevice('desktop')} label="Desktop">
              <Monitor className="h-4 w-4" /> Desktop
            </SegmentedItem>
          </Segmented>
        )}
        <Segmented label="Light or dark">
          <SegmentedItem active={mode === 'light'} onClick={() => setMode('light')} label="Light mode">
            <Sun className="h-4 w-4" />
          </SegmentedItem>
          <SegmentedItem active={mode === 'dark'} onClick={() => setMode('dark')} label="Dark mode">
            <Moon className="h-4 w-4" />
          </SegmentedItem>
        </Segmented>
        {!origin && <span className="text-xs text-muted-foreground">Sample menu</span>}
        <span className="flex-1" />
        {origin && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setReloadKey((k) => k + 1)} title="Reload preview" aria-label="Reload preview">
            <RotateCw className="h-4 w-4" />
          </Button>
        )}
        {openUrl && (
          <Button asChild variant="ghost" size="sm" className="gap-1.5" title="Open in new tab">
            <a href={openUrl} target="_blank" rel="noopener noreferrer" aria-label="Open in new tab">
              <ExternalLink className="h-4 w-4" /> <span className="hidden @5xl:inline">Open in new tab</span>
            </a>
          </Button>
        )}
      </div>

      <div ref={boxRef} className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
        <div className="relative shrink-0" style={{ width: outerW * scale, height: outerH * scale }}>
          <div
            className={cn(
              'absolute left-0 top-0 origin-top-left overflow-hidden shadow-xl',
              shown === 'phone' ? 'rounded-[2.75rem] ring-1 ring-border' : 'rounded-lg border bg-card',
            )}
            style={{
              width: outerW,
              height: outerH,
              padding: BEZEL[shown],
              transform: `scale(${scale})`,
              // The phone's body is the same dark glass in either admin theme.
              ...(shown === 'phone' ? { background: '#171717' } : null),
            }}
          >
            {shown === 'desktop' && (
              <div className="flex items-center gap-2 border-b bg-muted px-3" style={{ height: BROWSER_BAR }}>
                <span className="flex gap-1.5" aria-hidden>
                  <i className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
                  <i className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
                  <i className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
                </span>
                <span className="mx-auto w-1/2 truncate rounded-md bg-background px-3 py-1 text-center text-xs text-muted-foreground">
                  {origin?.replace(/^https?:\/\//, '')}
                </span>
              </div>
            )}
            <div className={cn('relative overflow-hidden bg-background', shown === 'phone' && 'rounded-[2.15rem]')} style={{ width: w, height: h }}>
              {origin ? (
                <>
                  <iframe
                    key={`${origin}-${reloadKey}`}
                    ref={frameRef}
                    title="Storefront preview"
                    src={`${origin}/?sf_reset`}
                    className="absolute inset-0 h-full w-full border-0 bg-background"
                  />
                  {!ready && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background p-8 text-center text-sm text-muted-foreground">
                      {slow
                        ? 'The live preview could not connect. Check the storefront address, and that this admin runs on the same address as your public POS link. Your changes still save normally.'
                        : 'Loading your storefront…'}
                    </div>
                  )}
                </>
              ) : (
                fallback(mode)
              )}
            </div>
          </div>
        </div>
      </div>

      {!origin || editing ? (
        <form
          className="shrink-0 space-y-2 border-t bg-card px-4 py-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (originOf(draftBase)) connect()
          }}
        >
          <label htmlFor="sf-base" className="block text-sm font-medium">
            {origin ? 'Your website address' : 'Preview your real menu'}
          </label>
          <div className="flex gap-2">
            <Input
              id="sf-base"
              type="url"
              inputMode="url"
              placeholder="https://order.yourcafe.com"
              value={draftBase}
              onChange={(e) => setDraftBase(e.target.value)}
            />
            <Button type="submit" disabled={!originOf(draftBase)}>
              Connect
            </Button>
            {editing && (
              <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Enter the address customers order from and this sample becomes your own site, restyled as you edit. The
            address is saved with your design and used for table QR codes.
          </p>
        </form>
      ) : (
        <p className="shrink-0 border-t bg-card px-4 py-2 text-xs text-muted-foreground">
          Your real menu with your unsaved changes. Customers see nothing until you save.{' '}
          <button type="button" className="underline underline-offset-2 hover:text-foreground" onClick={() => setEditing(true)}>
            Change address
          </button>
        </p>
      )}
    </div>
  )
}

function Segmented({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-1 rounded-lg bg-muted/60 p-1" role="group" aria-label={label}>
      {children}
    </div>
  )
}

function SegmentedItem({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean
  onClick: () => void
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      title={label}
      className={cn(
        'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}
